import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  FiArrowLeft,
  FiChevronRight,
  FiCalendar,
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiActivity,
  FiUsers,
  FiCheckCircle,
  FiAward,
  FiGrid,
  FiList,
  FiClock,
  FiShield,
  FiBell,
} from "react-icons/fi";
import { getEvents } from "../api/eventApi";
import { formatUtcDate, formatUtcDateTimeWithSeconds } from "../utils/dateUtils";
import EventFormModal from "./components/events/EventFormModal";
import EventStatusModal from "./components/events/EventStatusModal";
import EventDeleteModal from "./components/events/EventDeleteModal";

const STATUS_FILTERS = [
  { value: "all", label: "All Events" },
  { value: "running", label: "Running", dot: "bg-emerald-500" },
  { value: "upcoming", label: "Upcoming", dot: "bg-amber-500" },
  { value: "completed", label: "Completed", dot: "bg-blue-500" },
  { value: "noEvent", label: "No Event", dot: "bg-gray-400" },
];

const formatDate = (dateStr) => formatUtcDate(dateStr);


const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case "running":
      return {
        label: "Running",
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-500",
      };
    case "upcoming":
      return {
        label: "Upcoming",
        className: "bg-amber-50 text-amber-700 border-amber-200",
        dot: "bg-amber-500",
      };
    case "completed":
      return {
        label: "Completed",
        className: "bg-blue-50 text-blue-700 border-blue-200",
        dot: "bg-blue-500",
      };
    case "noevent":
    default:
      return {
        label: "No Event",
        className: "bg-gray-100 text-gray-600 border-gray-200",
        dot: "bg-gray-400",
      };
  }
};

const formatPrizeSummary = (prizes) => {
  if (!Array.isArray(prizes) || prizes.length === 0) return null;
  const top = prizes[0];
  const topZx = Number(top.rewardZx) || 0;
  const hasCoin =
    top.hasCoinReward != null
      ? Boolean(top.hasCoinReward)
      : Boolean(
          top.coinSymbol ||
            (top.rewardUsdt && Number(top.rewardUsdt) > 0) ||
            (top.coinAmount && Number(top.coinAmount) > 0)
        );
  const coinSymbol = top.coinSymbol || (top.rewardUsdt ? "USDT" : null);
  const coinAmount = Number(top.coinAmount ?? top.rewardUsdt ?? 0);

  if (hasCoin && coinSymbol && coinAmount > 0) {
    return `${coinAmount.toLocaleString()} ${coinSymbol} + ${topZx.toLocaleString()} ZX`;
  }
  return `${topZx.toLocaleString()} ZX`;
};

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState("table"); // "table" | "grid"
  const [currentUtcTime, setCurrentUtcTime] = useState(new Date());

  // Live ticking UTC clock for dashboard
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentUtcTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Modals state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusTargetEvent, setStatusTargetEvent] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetEvent, setDeleteTargetEvent] = useState(null);

  const handleViewParticipants = (event) => {
    navigate(`/events/${event.eventId || event.id}/participants`);
  };

  const debounceTimer = useRef(null);

  // Debounced search handler
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 400);
  };

  const fetchEventList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getEvents({
        page,
        limit,
        status: statusFilter,
        search: debouncedSearch,
      });

      const responseData = res?.data || res;
      const eventList =
        responseData?.events ||
        (Array.isArray(responseData) ? responseData : []);
      const pagination = responseData?.pagination || {};

      setEvents(eventList);
      setTotalEvents(pagination.total ?? eventList.length);
      const calculatedPages = Math.ceil((pagination.total || eventList.length) / limit) || 1;
      setTotalPages(pagination.totalPages ?? calculatedPages);
    } catch (err) {
      setError(err.message || "Failed to fetch events");
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchEventList();
  }, [fetchEventList]);

  // Metric aggregates
  const runningCount = events.filter((e) => e.status?.toLowerCase() === "running").length;
  const upcomingCount = events.filter((e) => e.status?.toLowerCase() === "upcoming").length;
  const completedCount = events.filter((e) => e.status?.toLowerCase() === "completed").length;
  const totalParticipants = events.reduce((acc, curr) => acc + (Number(curr.participantCount) || 0), 0);

  // Open modal helpers
  const handleCreateNew = () => {
    setEditingEvent(null);
    setFormModalOpen(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setFormModalOpen(true);
  };

  const handleViewDetails = (idOrEvent) => {
    const id = typeof idOrEvent === "object" ? idOrEvent.eventId || idOrEvent.id : idOrEvent;
    navigate(`/events/${id}`);
  };

  const handleChangeStatus = (event) => {
    setStatusTargetEvent(event);
    setStatusModalOpen(true);
  };

  const handleDeleteEvent = (event) => {
    setDeleteTargetEvent(event);
    setDeleteModalOpen(true);
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-4 sm:p-8 space-y-6">
          {/* Breadcrumbs & Header */}
          <div className="space-y-3">
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
              <Link
                to="/dashboard"
                className="hover:text-black hover:underline transition flex items-center gap-1"
              >
                Dashboard
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
                All Events
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/dashboard");
                    }
                  }}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                  title="Back to Previous Page"
                  aria-label="Back"
                >
                  <FiArrowLeft size={18} />
                </button>
                <div>
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                    Events Management
                  </h1>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Create, configure, and monitor community reward events &amp; prize pools
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 self-start sm:self-auto flex-wrap">
                {/* Live UTC Clock Pill */}
                <div className="hidden md:flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 rounded-xl shadow-xs text-xs">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wide">UTC:</span>
                  <span className="font-mono font-bold text-gray-800">
                    {formatUtcDateTimeWithSeconds(currentUtcTime)}
                  </span>
                </div>

                <button
                  onClick={fetchEventList}
                  disabled={loading}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-600 transition shadow-sm cursor-pointer"
                  title="Refresh List"
                >
                  <FiRefreshCw size={16} className={loading ? "animate-spin" : ""} />
                </button>
                <button
                  onClick={() => navigate("/events/winners")}
                  className="flex items-center gap-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 text-sm font-bold px-4 py-2.5 rounded-xl shadow-sm transition cursor-pointer"
                >
                  <FiAward size={16} /> Winner Rewards
                </button>
                <button
                  onClick={() => navigate("/events/notifications")}
                  className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-sm font-bold px-4 py-2.5 rounded-xl shadow-sm transition cursor-pointer"
                >
                  <FiBell size={16} /> Event Notifications
                </button>
                <button
                  onClick={handleCreateNew}
                  className="flex items-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] text-black text-sm font-bold px-5 py-2.5 rounded-xl shadow-sm transition transform active:scale-95 cursor-pointer"
                >
                  <FiPlus size={16} /> Create Event
                </button>
              </div>
            </div>
          </div>

          {/* Metric Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Total Events
                </p>
                <p className="text-2xl font-black text-gray-800 mt-1">
                  {totalEvents.toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center">
                <FiCalendar size={20} />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Upcoming
                </p>
                <p className="text-2xl font-black text-amber-600 mt-1">
                  {upcomingCount.toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <FiClock size={20} />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Active (Running)
                </p>
                <p className="text-2xl font-black text-emerald-600 mt-1">
                  {runningCount.toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <FiActivity size={20} />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Completed
                </p>
                <p className="text-2xl font-black text-blue-600 mt-1">
                  {completedCount.toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <FiCheckCircle size={20} />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex items-center justify-between col-span-2 sm:col-span-1">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Participants
                </p>
                <p className="text-2xl font-black text-purple-600 mt-1">
                  {totalParticipants.toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                <FiUsers size={20} />
              </div>
            </div>
          </div>

          {/* Controls Bar: Search + Status Filter Pills + View Mode */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <FiSearch
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search by event name, ID, or description..."
                value={search}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
              />
            </div>

            {/* Filter Pills + View Switcher */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center bg-gray-100 p-1 rounded-xl">
                {STATUS_FILTERS.map((f) => {
                  const active = statusFilter === f.value;
                  return (
                    <button
                      key={f.value}
                      onClick={() => {
                        setStatusFilter(f.value);
                        setPage(1);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${
                        active
                          ? "bg-white text-black shadow-sm"
                          : "text-gray-500 hover:text-gray-800"
                      }`}
                    >
                      {f.dot && <span className={`w-2 h-2 rounded-full ${f.dot}`} />}
                      {f.label}
                    </button>
                  );
                })}
              </div>

              {/* View Toggle */}
              <div className="hidden sm:flex items-center bg-gray-100 p-1 rounded-xl">
                <button
                  onClick={() => setViewMode("table")}
                  className={`p-1.5 rounded-lg transition ${
                    viewMode === "table" ? "bg-white text-black shadow-sm" : "text-gray-400 hover:text-gray-700"
                  }`}
                  title="Table View"
                >
                  <FiList size={16} />
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-1.5 rounded-lg transition ${
                    viewMode === "grid" ? "bg-white text-black shadow-sm" : "text-gray-400 hover:text-gray-700"
                  }`}
                  title="Card Grid View"
                >
                  <FiGrid size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Main Events Content */}
          {loading && events.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 flex flex-col items-center justify-center gap-3">
              <div className="w-10 h-10 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-gray-400 font-medium">Loading events...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-6 text-sm flex flex-col items-center gap-3 text-center">
              <p>{error}</p>
              <button
                onClick={fetchEventList}
                className="px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-semibold hover:bg-red-700 transition"
              >
                Retry
              </button>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center space-y-4 shadow-sm">
              <div className="w-16 h-16 rounded-full bg-yellow-50 text-[#999900] flex items-center justify-center mx-auto text-2xl font-bold">
                🎉
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">No Events Found</h3>
                <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                  {search || statusFilter !== "all"
                    ? "Try adjusting your search criteria or status filter to find existing events."
                    : "Get started by creating your first community event with rewards and terms."}
                </p>
              </div>
              <button
                onClick={handleCreateNew}
                className="inline-flex items-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] text-black text-xs font-bold px-4 py-2 rounded-xl transition"
              >
                <FiPlus size={14} /> Create Event
              </button>
            </div>
          ) : viewMode === "table" ? (
            /* Table View */
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-gray-50/80 text-[11px] text-gray-400 uppercase font-semibold tracking-wider border-b border-gray-100">
                      <th className="px-5 py-3.5 w-12">#</th>
                      <th className="px-5 py-3.5">Event Info</th>
                      <th className="px-5 py-3.5">Status</th>
                      <th className="px-5 py-3.5">Schedule</th>
                      <th className="px-5 py-3.5 text-center">Participants</th>
                      <th className="px-5 py-3.5">Prize Pool</th>
                      <th className="px-5 py-3.5 text-center w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {events.map((event, idx) => {
                      const statusBadge = getStatusBadge(event.status);
                      const prizeCount = Array.isArray(event.prizes) ? event.prizes.length : 0;
                      const prizeSummary = formatPrizeSummary(event.prizes);

                      return (
                        <tr
                          key={event.id || event.eventId || idx}
                          onClick={() => handleViewDetails(event)}
                          className="hover:bg-yellow-50/30 transition cursor-pointer group"
                        >
                          {/* Index */}
                          <td className="px-5 py-4 text-xs font-mono text-gray-400">
                            {(page - 1) * limit + idx + 1}
                          </td>

                          {/* Event Info */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold flex-shrink-0 text-sm group-hover:scale-105 transition-transform">
                                🏆
                              </div>
                              <div className="min-w-0">
                                <span className="font-bold text-gray-800 group-hover:text-black group-hover:underline truncate max-w-xs block">
                                  {event.eventName}
                                </span>
                                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                  <p className="text-[11px] text-gray-400 font-mono truncate max-w-xs">
                                    {event.eventId}
                                  </p>
                                  {(event.checkInCoin != null || event.checkinCoin != null || event.checkInPoints != null || event.coin != null) && (
                                    <span className="text-[10px] font-semibold text-[#999900] bg-[#ffffcc] px-1.5 py-0.5 rounded">
                                      🪙 {(event.checkInCoin ?? event.checkinCoin ?? event.checkInPoints ?? event.coin)}/check-in
                                    </span>
                                  )}
                                  {(event.referralCoin != null || event.referralCoins != null || event.referralPoints != null || event.referralBonus != null) && (
                                    <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200/60 px-1.5 py-0.5 rounded">
                                      👥 {(event.referralCoin ?? event.referralCoins ?? event.referralPoints ?? event.referralBonus)}/referral
                                    </span>
                                  )}
                                  {(event.miningCycleCoin != null || event.miningCycleCoins != null || event.miningCycle != null || event.miningCyclePoints != null || event.miningCoin != null) && (
                                    <span className="text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200/60 px-1.5 py-0.5 rounded">
                                      ⚡ {(event.miningCycleCoin ?? event.miningCycleCoins ?? event.miningCycle ?? event.miningCyclePoints ?? event.miningCoin)}/cycle
                                    </span>
                                  )}
                                  {(event.browserPointsPerZx != null || event.browserPointsRate != null) && (
                                    <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200/60 px-1.5 py-0.5 rounded">
                                      🌐 {(event.browserPointsPerZx ?? event.browserPointsRate)}/ZX
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-5 py-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChangeStatus(event);
                              }}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition hover:opacity-80 ${statusBadge.className}`}
                              title="Click to change status"
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`} />
                              {statusBadge.label}
                            </button>
                          </td>

                          {/* Schedule */}
                          <td className="px-5 py-4">
                            <div className="text-xs text-gray-600 space-y-0.5 whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <FiClock size={12} className="text-emerald-500" />
                                <span>{formatDate(event.startDate)}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-gray-400">
                                <span className="text-[10px] pl-4">to {formatDate(event.endDate)}</span>
                              </div>
                            </div>
                          </td>

                          {/* Participants */}
                          <td className="px-5 py-4 text-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewParticipants(event);
                              }}
                              className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-[#ffffcc] hover:text-black hover:border-yellow-300 border border-gray-200 px-3 py-1.5 rounded-lg transition shadow-xs cursor-pointer group/btn"
                              title="Click to view participant users"
                            >
                              <FiUsers size={13} className="text-gray-500 group-hover/btn:text-black" />
                              <span>{(event.participantCount ?? 0).toLocaleString()}</span>
                            </button>
                          </td>

                          {/* Prize Pool */}
                          <td className="px-5 py-4">
                            <div className="text-xs">
                              {prizeCount > 0 ? (
                                <div className="space-y-0.5">
                                  <span className="font-semibold text-gray-700 block">
                                    {prizeCount} Tier{prizeCount > 1 ? "s" : ""}
                                  </span>
                                  <span className="text-[11px] text-emerald-600 block font-mono font-medium">
                                    Top: {prizeSummary}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewParticipants(event);
                                }}
                                className="p-1.5 rounded-lg text-amber-700 hover:bg-amber-50 transition"
                                title="View Participant Users"
                              >
                                <FiUsers size={15} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/events/${event.eventId || event.id}/winners`);
                                }}
                                className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50 transition"
                                title="Winner Rewards & Approvals"
                              >
                                <FiAward size={15} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetails(event);
                                }}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition"
                                title="View Details"
                              >
                                <FiEye size={15} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditEvent(event);
                                }}
                                className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 transition"
                                title="Edit Event"
                              >
                                <FiEdit2 size={15} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangeStatus(event);
                                }}
                                className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition"
                                title="Change Status"
                              >
                                <FiActivity size={15} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteEvent(event);
                                }}
                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition"
                                title="Delete Event"
                              >
                                <FiTrash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {events.map((event, idx) => {
                const statusBadge = getStatusBadge(event.status);
                const prizeCount = Array.isArray(event.prizes) ? event.prizes.length : 0;
                const prizeSummary = formatPrizeSummary(event.prizes);

                return (
                  <div
                    key={event.id || event.eventId || idx}
                    onClick={() => handleViewDetails(event)}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col justify-between hover:shadow-md hover:border-yellow-200 transition cursor-pointer group"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusBadge.className}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`} />
                          {statusBadge.label}
                        </span>
                        <span className="text-[11px] text-gray-400 font-mono">
                          {event.eventId}
                        </span>
                      </div>

                      <h3
                        className="text-base font-bold text-gray-800 group-hover:text-black group-hover:underline leading-snug line-clamp-1"
                      >
                        {event.eventName}
                      </h3>

                      <p className="text-xs text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">
                        {event.description || "No description provided."}
                      </p>

                      {/* Info Pills */}
                      <div className="mt-4 pt-3 border-t border-gray-100 space-y-2 text-xs text-gray-600">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 flex items-center gap-1">
                            <FiCalendar size={13} /> Timeline
                          </span>
                          <span className="font-medium">
                            {formatDate(event.startDate)} - {formatDate(event.endDate)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 flex items-center gap-1">
                            <FiUsers size={13} /> Participants
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewParticipants(event);
                            }}
                            className="font-bold text-gray-800 hover:text-black hover:bg-[#ffffcc] bg-gray-100 px-2 py-0.5 rounded transition text-xs flex items-center gap-1"
                            title="Click to view participant users"
                          >
                            <span>{(event.participantCount ?? 0).toLocaleString()}</span>
                            <span className="text-[10px] text-amber-700 font-semibold underline">Users</span>
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 flex items-center gap-1">
                            <FiAward size={13} /> Prize Pool
                          </span>
                          <span className="font-semibold text-emerald-600 truncate max-w-[180px]" title={prizeSummary || `${prizeCount} Tiers`}>
                            {prizeSummary ? prizeSummary : `${prizeCount} Tier${prizeCount > 1 ? "s" : ""}`}
                          </span>
                        </div>
                        {(event.checkInCoin != null || event.checkinCoin != null || event.checkInPoints != null || event.coin != null) && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 flex items-center gap-1">
                              <span>🪙</span> Check-in Coin
                            </span>
                            <span className="font-semibold text-[#999900]">
                              {(event.checkInCoin ?? event.checkinCoin ?? event.checkInPoints ?? event.coin).toLocaleString()} coins
                            </span>
                          </div>
                        )}
                        {(event.referralCoin != null || event.referralCoins != null || event.referralPoints != null || event.referralBonus != null) && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 flex items-center gap-1">
                              <span>👥</span> Referral Coin
                            </span>
                            <span className="font-semibold text-blue-700">
                              {(event.referralCoin ?? event.referralCoins ?? event.referralPoints ?? event.referralBonus).toLocaleString()} coins
                            </span>
                          </div>
                        )}
                        {(event.miningCycleCoin != null || event.miningCycleCoins != null || event.miningCycle != null || event.miningCyclePoints != null || event.miningCoin != null) && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 flex items-center gap-1">
                              <span>⚡</span> Mining Cycle Coin
                            </span>
                            <span className="font-semibold text-teal-700">
                              {(event.miningCycleCoin ?? event.miningCycleCoins ?? event.miningCycle ?? event.miningCyclePoints ?? event.miningCoin).toLocaleString()} coins
                            </span>
                          </div>
                        )}
                        {(event.browserPointsPerZx != null || event.browserPointsRate != null) && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 flex items-center gap-1">
                              <span>🌐</span> Browser Points Rate
                            </span>
                            <span className="font-semibold text-indigo-700">
                              {(event.browserPointsPerZx ?? event.browserPointsRate).toLocaleString()} pts / ZX
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div className="flex items-center justify-between gap-1.5 mt-5 pt-3 border-t border-gray-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(event);
                        }}
                        className="flex-1 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition text-center"
                      >
                        View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewParticipants(event);
                        }}
                        className="px-2 py-1.5 text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg transition text-center flex items-center gap-1"
                        title="View Participant Users"
                      >
                        <FiUsers size={13} /> Users
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/events/${event.eventId || event.id}/winners`);
                        }}
                        className="px-2 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition text-center flex items-center gap-1"
                        title="Winner Rewards & Approvals"
                      >
                        <FiAward size={13} /> Rewards
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEvent(event);
                        }}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition"
                        title="Edit"
                      >
                        <FiEdit2 size={15} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChangeStatus(event);
                        }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="Status"
                      >
                        <FiActivity size={15} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteEvent(event);
                        }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition"
                        title="Delete"
                      >
                        <FiTrash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Show</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1 border border-gray-200 rounded-lg bg-white text-xs font-medium"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span>items per page • Total {totalEvents} events</span>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-xs font-semibold border rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  &laquo; Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                  .map((pg, idx, arr) => {
                    const prev = arr[idx - 1];
                    const isEllipsis = prev && pg - prev > 1;
                    return (
                      <React.Fragment key={pg}>
                        {isEllipsis && <span className="px-1 text-xs text-gray-400">...</span>}
                        <button
                          onClick={() => setPage(pg)}
                          className={`px-3 py-1.5 text-xs font-bold border rounded-lg transition ${
                            page === pg
                              ? "bg-[#ffff00] text-black border-[#ffff00]"
                              : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"
                          }`}
                        >
                          {pg}
                        </button>
                      </React.Fragment>
                    );
                  })}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 text-xs font-semibold border rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next &raquo;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <EventFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingEvent(null);
        }}
        onSuccess={fetchEventList}
        initialEvent={editingEvent}
      />

      <EventStatusModal
        isOpen={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          setStatusTargetEvent(null);
        }}
        onSuccess={fetchEventList}
        event={statusTargetEvent}
      />

      <EventDeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTargetEvent(null);
        }}
        onSuccess={fetchEventList}
        event={deleteTargetEvent}
      />
    </div>
  );
};

export default Events;
